import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { MobileMenuProvider } from "@/contexts/MobileMenuContext";
import ScrollToTop from "@/components/ScrollToTop";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import LanguageWrapper from "@/components/LanguageWrapper";
import LanguageRedirect from "@/components/LanguageRedirect";

// Eager: homepage only
import Index from "./pages/Index";

// Lazy: everything else
const StorePage = lazy(() => import("./pages/StorePage"));
const AgentDetail = lazy(() => import("./pages/AgentDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const BecomeSeller = lazy(() => import("./pages/BecomeSeller"));
const CreateAgent = lazy(() => import("./pages/CreateAgent"));
const EditAgent = lazy(() => import("./pages/EditAgent"));
const SellerProfile = lazy(() => import("./pages/SellerProfile"));
const Account = lazy(() => import("./pages/Account"));
const AgentChat = lazy(() => import("./pages/AgentChat"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Schedule = lazy(() => import("./pages/Schedule"));
const EnterpriseSchedule = lazy(() => import("./pages/EnterpriseSchedule"));
const ContactSales = lazy(() => import("./pages/ContactSales"));
const LiveDemo = lazy(() => import("./pages/LiveDemo"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutAccount = lazy(() => import("./pages/CheckoutAccount"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Partners = lazy(() => import("./pages/Partners"));
const WhiteLabel = lazy(() => import("./pages/WhiteLabel"));
const Affiliates = lazy(() => import("./pages/Affiliates"));

// Seller Dashboard
const SellerLayout = lazy(() => import("./layouts/SellerLayout"));
const SellerHome = lazy(() => import("./pages/seller/SellerHome"));
const SellerProducts = lazy(() => import("./pages/seller/SellerProducts"));
const SellerOrders = lazy(() => import("./pages/seller/SellerOrders"));
const SellerOrderDetail = lazy(() => import("./pages/seller/SellerOrderDetail"));
const SellerCustomers = lazy(() => import("./pages/seller/SellerCustomers"));
const SellerCustomerDetail = lazy(() => import("./pages/seller/SellerCustomerDetail"));
const SellerAnalytics = lazy(() => import("./pages/seller/SellerAnalytics"));
const SellerLiveView = lazy(() => import("./pages/seller/SellerLiveView"));
const SellerFinance = lazy(() => import("./pages/seller/SellerFinance"));
const SellerPayouts = lazy(() => import("./pages/seller/SellerPayouts"));
const SellerMessages = lazy(() => import("./pages/seller/SellerMessages"));
const SellerSettings = lazy(() => import("./pages/seller/SellerSettings"));
const SellerTeam = lazy(() => import("./pages/seller/SellerTeam"));
const SellerCampaigns = lazy(() => import("./pages/seller/SellerCampaigns"));
const SellerReports = lazy(() => import("./pages/seller/SellerReports"));

// Platform pages
const AgentMarketplace = lazy(() => import("./pages/platform/AgentMarketplace"));
const SmartCRM = lazy(() => import("./pages/platform/SmartCRM"));
const SmartBooking = lazy(() => import("./pages/platform/SmartBooking"));
const VoiceAI = lazy(() => import("./pages/platform/VoiceAI"));
const MotionOS = lazy(() => import("./pages/platform/MotionOS"));
const XpandMemory = lazy(() => import("./pages/platform/XpandMemory"));

// Solutions pages
const Hospitality = lazy(() => import("./pages/solutions/Hospitality"));
const RealEstate = lazy(() => import("./pages/solutions/RealEstate"));
const Hotels = lazy(() => import("./pages/solutions/Hotels"));
const BeautySalons = lazy(() => import("./pages/solutions/BeautySalons"));
const Technology = lazy(() => import("./pages/solutions/Technology"));
const Healthcare = lazy(() => import("./pages/solutions/Healthcare"));
const TravelTourism = lazy(() => import("./pages/solutions/TravelTourism"));
const Marketing = lazy(() => import("./pages/solutions/Marketing"));
const Sales = lazy(() => import("./pages/solutions/Sales"));
const LegalFinance = lazy(() => import("./pages/solutions/LegalFinance"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminListings = lazy(() => import("./pages/admin/AdminListings"));
const AdminSellers = lazy(() => import("./pages/admin/AdminSellers"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminTickets = lazy(() => import("./pages/admin/AdminTickets"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminPartners = lazy(() => import("./pages/admin/AdminPartners"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const AdminDemoUsers = lazy(() => import("./pages/admin/AdminDemoUsers"));
const AdminStatus = lazy(() => import("./pages/admin/AdminStatus"));

// Support pages
const SupportDashboard = lazy(() => import("./pages/support/SupportDashboard"));
const SupportUsers = lazy(() => import("./pages/support/SupportUsers"));
const SupportOrders = lazy(() => import("./pages/support/SupportOrders"));

// Resources pages
const Documentation = lazy(() => import("./pages/Documentation"));
const DocArticle = lazy(() => import("./pages/DocArticle"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));

const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Status = lazy(() => import("./pages/Status"));
const VoiceAILanding = lazy(() => import("./pages/landing/VoiceAILanding"));
const ClientDemo = lazy(() => import("./pages/ClientDemo"));

const queryClient = new QueryClient();

// Minimal loading fallback - matches site background
function PageFallback() {
  return <div className="min-h-screen bg-[#050510]" />;
}

/**
 * Public SEO routes - these are duplicated inside /:lang wrapper
 * for proper multilingual URL structure (/en/pricing, /es/pricing)
 */
function PublicRoutes() {
  return (
    <>
      <Route index element={<Index />} />
      <Route path="store" element={<StorePage />} />
      <Route path="store/:slug" element={<AgentDetail />} />
      <Route path="marketplace" element={<StorePage />} />
      <Route path="marketplace/:slug" element={<AgentDetail />} />
      <Route path="pricing" element={<Pricing />} />
      <Route path="schedule" element={<Schedule />} />
      <Route path="enterprise" element={<EnterpriseSchedule />} />
      <Route path="contact-sales" element={<ContactSales />} />
      <Route path="live-demo" element={<LiveDemo />} />
      <Route path="partners" element={<Partners />} />
      <Route path="white-label" element={<WhiteLabel />} />
      <Route path="affiliates" element={<Affiliates />} />
      <Route path="landing" element={<VoiceAILanding />} />
      <Route path="become-seller" element={<BecomeSeller />} />
      <Route path="auth" element={<Auth />} />

      {/* Platform routes */}
      <Route path="platform/agent-hub" element={<AgentMarketplace />} />
      <Route path="platform/smart-crm" element={<SmartCRM />} />
      <Route path="platform/smart-booking" element={<SmartBooking />} />
      <Route path="platform/voice-ai" element={<VoiceAI />} />
      <Route path="platform/xpand-os" element={<MotionOS />} />
      <Route path="platform/xpand-memory" element={<XpandMemory />} />

      {/* Solutions routes */}
      <Route path="solutions/hospitality" element={<Hospitality />} />
      <Route path="solutions/real-estate" element={<RealEstate />} />
      <Route path="solutions/hotels" element={<Hotels />} />
      <Route path="solutions/beauty-salons" element={<BeautySalons />} />
      <Route path="solutions/technology" element={<Technology />} />
      <Route path="solutions/healthcare" element={<Healthcare />} />
      <Route path="solutions/travel-tourism" element={<TravelTourism />} />
      <Route path="solutions/marketing" element={<Marketing />} />
      <Route path="solutions/sales" element={<Sales />} />
      <Route path="solutions/legal-finance" element={<LegalFinance />} />

      {/* Resources routes */}
      <Route path="docs" element={<Documentation />} />
      <Route path="docs/:slug" element={<DocArticle />} />
      <Route path="blog" element={<Blog />} />
      <Route path="blog/:slug" element={<BlogArticle />} />

      <Route path="terms" element={<Terms />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="cookies" element={<Cookies />} />
      <Route path="status" element={<Status />} />
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <MobileMenuProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsProvider>
            <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Language-prefixed public routes: /en/... and /es/... */}
              <Route path="/:lang" element={<LanguageWrapper />}>
                {PublicRoutes()}
              </Route>

              {/* Client demo (standalone, no nav) */}
              <Route path="/demo/:slug" element={<ClientDemo />} />

              {/* Non-localized app routes (auth-gated, no SEO needed) */}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/checkout/account" element={<CheckoutAccount />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
              <Route path="/account" element={<Account />} />
              <Route path="/chat/:slug" element={<AgentChat />} />

              {/* Seller Dashboard - Isolated Layout */}
              <Route path="/seller" element={<SellerLayout />}>
                <Route index element={<SellerHome />} />
                <Route path="products" element={<SellerProducts />} />
                <Route path="products/new" element={<CreateAgent />} />
                <Route path="products/:slug" element={<EditAgent />} />
                <Route path="orders" element={<SellerOrders />} />
                <Route path="orders/:id" element={<SellerOrderDetail />} />
                <Route path="customers" element={<SellerCustomers />} />
                <Route path="customers/:id" element={<SellerCustomerDetail />} />
                <Route path="analytics" element={<SellerAnalytics />} />
                <Route path="reports" element={<SellerReports />} />
                <Route path="live" element={<SellerLiveView />} />
                <Route path="finance" element={<SellerFinance />} />
                <Route path="payouts" element={<SellerPayouts />} />
                <Route path="messages" element={<SellerMessages />} />
                <Route path="settings" element={<SellerSettings />} />
                <Route path="team" element={<SellerTeam />} />
                <Route path="campaigns" element={<SellerCampaigns />} />
              </Route>

              <Route path="/seller/:handle" element={<SellerProfile />} />
              <Route path="/seller/create-agent" element={<CreateAgent />} />
              <Route path="/seller/edit-agent/:slug" element={<EditAgent />} />

              {/* Admin routes */}
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/listings" element={<AdminListings />} />
              <Route path="/admin/sellers" element={<AdminSellers />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
              <Route path="/admin/tickets" element={<AdminTickets />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/payments" element={<AdminPayments />} />
              <Route path="/admin/affiliates" element={<AdminAffiliates />} />
              <Route path="/admin/partners" element={<AdminPartners />} />
              <Route path="/admin/analytics" element={<AdminAnalytics />} />
              <Route path="/admin/calendar" element={<AdminCalendar />} />
              <Route path="/admin/demo-users" element={<AdminDemoUsers />} />
              <Route path="/admin/status" element={<AdminStatus />} />

              {/* Support routes */}
              <Route path="/support" element={<SupportDashboard />} />
              <Route path="/support/users" element={<SupportUsers />} />
              <Route path="/support/orders" element={<SupportOrders />} />

              {/* Legacy bare-path redirects → localized versions */}
              <Route path="/" element={<LanguageRedirect />} />
              <Route path="/store" element={<LanguageRedirect />} />
              <Route path="/store/:slug" element={<LanguageRedirect />} />
              <Route path="/marketplace" element={<LanguageRedirect />} />
              <Route path="/marketplace/:slug" element={<LanguageRedirect />} />
              <Route path="/pricing" element={<LanguageRedirect />} />
              <Route path="/schedule" element={<LanguageRedirect />} />
              <Route path="/enterprise" element={<LanguageRedirect />} />
              <Route path="/contact-sales" element={<LanguageRedirect />} />
              <Route path="/live-demo" element={<LanguageRedirect />} />
              <Route path="/partners" element={<LanguageRedirect />} />
              <Route path="/white-label" element={<LanguageRedirect />} />
              <Route path="/affiliates" element={<LanguageRedirect />} />
              <Route path="/landing" element={<LanguageRedirect />} />
              <Route path="/become-seller" element={<LanguageRedirect />} />
              <Route path="/auth" element={<LanguageRedirect />} />
              <Route path="/platform/*" element={<LanguageRedirect />} />
              <Route path="/solutions/*" element={<LanguageRedirect />} />
              <Route path="/docs" element={<LanguageRedirect />} />
              <Route path="/docs/:slug" element={<LanguageRedirect />} />
              <Route path="/blog" element={<LanguageRedirect />} />
              <Route path="/blog/:slug" element={<LanguageRedirect />} />
              <Route path="/terms" element={<LanguageRedirect />} />
              <Route path="/privacy" element={<LanguageRedirect />} />
              <Route path="/cookies" element={<LanguageRedirect />} />
              <Route path="/status" element={<LanguageRedirect />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </AnalyticsProvider>
          </BrowserRouter>
        </TooltipProvider>
      </MobileMenuProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
